import { useCallback, useEffect, useState } from 'react';
import {
  limpezaApi,
  type CategoriaLimpeza,
  type ResultadoLimpeza,
  type ResumoLimpeza,
} from '@/features/configuracoes/api/limpezaApi';
import { Button } from '@/shared/ui/Button';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Toast, type TipoToast } from '@/shared/ui/Toast';
import { useDialogos } from '@/shared/ui/DialogosProvider';

const CONFIRMACAO = 'LIMPAR';

type Cartao = {
  categoria: CategoriaLimpeza;
  titulo: string;
  descricao: string;
  /** Rótulo no singular/plural do que está sendo contado. */
  unidade: [string, string];
};

const CARTOES: Cartao[] = [
  {
    categoria: 'ordens',
    titulo: 'Ordens de serviço',
    descricao:
      'Apaga as ordens de serviço com seus itens, os lançamentos financeiros que nasceram delas e o KM que registraram nos veículos. Peças, serviços, clientes e o estoque ficam como estão.',
    unidade: ['ordem de serviço', 'ordens de serviço'],
  },
  {
    categoria: 'vendas',
    titulo: 'Vendas do PDV',
    descricao:
      'Apaga as vendas de balcão com seus itens e pagamentos, os caixas e os lançamentos de fechamento no financeiro. Peças, clientes e o estoque ficam como estão.',
    unidade: ['venda', 'vendas'],
  },
  {
    categoria: 'financeiro',
    titulo: 'Financeiro avulso',
    descricao:
      'Apaga os lançamentos a pagar e a receber que você criou direto no financeiro, com baixas e itens. Os que vieram de uma ordem de serviço saem junto com ela.',
    unidade: ['lançamento', 'lançamentos'],
  },
  {
    categoria: 'clientes',
    titulo: 'Clientes',
    descricao:
      'Apaga os clientes junto com os veículos deles, o histórico de KM e os lançamentos financeiros no nome do cliente.',
    unidade: ['cliente', 'clientes'],
  },
  {
    categoria: 'pecas',
    titulo: 'Peças',
    descricao:
      'Apaga o catálogo de peças junto com o saldo e todo o histórico de movimentação de estoque delas.',
    unidade: ['peça', 'peças'],
  },
  {
    categoria: 'servicos',
    titulo: 'Serviços',
    descricao: 'Apaga o catálogo de serviços.',
    unidade: ['serviço', 'serviços'],
  },
  {
    categoria: 'colaboradores',
    titulo: 'Colaboradores e ponto',
    descricao: 'Apaga os colaboradores junto com as batidas do controle de ponto.',
    unidade: ['colaborador', 'colaboradores'],
  },
];

function resumirResultado(r: ResultadoLimpeza): string {
  const partes: string[] = [];
  if (r.ordens) partes.push(`${r.ordens} ordem(ns) de serviço`);
  if (r.ordensItens) partes.push(`${r.ordensItens} item(ns) de OS`);
  if (r.financeiros) partes.push(`${r.financeiros} lançamento(s) financeiro(s)`);
  if (r.clientes) partes.push(`${r.clientes} cliente(s)`);
  if (r.veiculos) partes.push(`${r.veiculos} veículo(s)`);
  if (r.pecas) partes.push(`${r.pecas} peça(s)`);
  if (r.movimentacoesEstoque) {
    partes.push(`${r.movimentacoesEstoque} movimentação(ões) de estoque`);
  }
  if (r.servicos) partes.push(`${r.servicos} serviço(s)`);
  if (r.colaboradores) partes.push(`${r.colaboradores} colaborador(es)`);
  if (r.batidasPonto) partes.push(`${r.batidasPonto} batida(s) de ponto`);
  return partes.length > 0 ? partes.join(', ') : 'nada — não havia registros';
}

export function LimpezaDadosPage({ onVoltar }: { onVoltar: () => void }) {
  const [resumo, setResumo] = useState<ResumoLimpeza | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [limpando, setLimpando] = useState<CategoriaLimpeza | null>(null);
  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoToast } | null>(null);
  const dialogos = useDialogos();

  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setResumo(await limpezaApi.resumo());
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar os totais.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function limpar(cartao: Cartao, dado: { registros: number }): Promise<void> {
    const digitado = await dialogos.solicitar({
      titulo: `Limpar ${cartao.titulo.toLowerCase()}`,
      mensagem:
        `Você vai apagar ${dado.registros} registro(s) desta máquina e do servidor. ` +
        'Não dá para desfazer.\n\n' +
        `Digite ${CONFIRMACAO} para confirmar:`,
      placeholder: CONFIRMACAO,
      textoConfirmar: 'Limpar agora',
    });
    if (digitado === null) return;
    if (digitado.trim().toUpperCase() !== CONFIRMACAO) {
      setToast({ mensagem: 'Confirmação não confere — nada foi apagado.', tipo: 'erro' });
      return;
    }

    setLimpando(cartao.categoria);
    try {
      const r = await limpezaApi.executar([cartao.categoria]);
      setToast({ mensagem: `Apagado: ${resumirResultado(r)}.`, tipo: 'sucesso' });
      await recarregar();
    } catch (e) {
      setToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao limpar os dados.',
        tipo: 'erro',
      });
    } finally {
      setLimpando(null);
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Limpar dados"
        descricao="Apaga os dados de teste categoria por categoria, sem mexer na sua conta, na assinatura nem nos dados da empresa."
      />

      <Toast
        mensagem={toast?.mensagem ?? null}
        tipo={toast?.tipo}
        onFechar={() => setToast(null)}
      />

      <div className="row-actions" style={{ marginBottom: 12 }}>
        <Button onClick={onVoltar}>← Voltar para Configurações</Button>
      </div>

      <div className="card">
        <div className="card__body">
          <p className="hint" style={{ marginTop: 0 }}>
            Serve para começar do zero depois de uma apresentação: você apaga só
            o que quer e mantém o resto. A limpeza vale para esta máquina{' '}
            <strong>e para o servidor</strong> — as outras máquinas da mesma conta
            recebem a exclusão na próxima sincronização. <strong>Não tem desfazer</strong>;
            se quiser guardar o estado atual, faça um backup antes em Backup na nuvem.
          </p>
        </div>
      </div>

      {erro && (
        <p role="alert" className="alert alert--error">
          {erro}
        </p>
      )}

      {carregando && !resumo ? (
        <p className="hint">Carregando totais…</p>
      ) : (
        resumo &&
        CARTOES.map((cartao) => {
          const dado = resumo[cartao.categoria];
          const vazio = dado.registros === 0;
          const bloqueado = Boolean(dado.bloqueio);
          const ocupado = limpando !== null;
          return (
            <div className="card" key={cartao.categoria}>
              <div className="card__header">
                <span className="card__title">{cartao.titulo}</span>
                <span className="hint">
                  {dado.registros}{' '}
                  {dado.registros === 1 ? cartao.unidade[0] : cartao.unidade[1]}
                </span>
              </div>
              <div className="card__body">
                <p className="hint" style={{ marginTop: 0 }}>
                  {cartao.descricao}
                </p>

                {dado.detalhes.length > 0 && (
                  <p className="hint" style={{ marginTop: 4 }}>
                    Sai junto: {dado.detalhes.join(' · ')}.
                  </p>
                )}

                {bloqueado && !vazio && (
                  <p role="alert" className="alert alert--error" style={{ marginTop: 8 }}>
                    {dado.bloqueio}
                  </p>
                )}

                <div className="row-actions" style={{ marginTop: 12 }}>
                  <Button
                    variante="primary"
                    onClick={() => void limpar(cartao, dado)}
                    disabled={vazio || bloqueado || ocupado}
                  >
                    {limpando === cartao.categoria
                      ? 'Limpando…'
                      : `Excluir ${cartao.titulo.toLowerCase()}`}
                  </Button>
                  {vazio && <span className="hint">Nada para apagar aqui.</span>}
                </div>
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}
